/**
 * Parsing of Identity Platform email-action links on the explicit
 * `/auth/action` route (pure, no React, no Firebase).
 *
 * Identity Platform sends every account email (password reset, email
 * verification, email change/recovery, MFA revert) to one action URL with
 * `?mode=…&oobCode=…&tenantId=…`. On `/auth/action` the first path segment is
 * `auth`, which `parseTenantSlug` would otherwise read as a tenant slug — so
 * this route is dispatched in `App()` before any tenant boot, for every mode.
 */
export const AUTH_ACTION_PATH = "/auth/action";

/** The non-reset modes, all consumed with `checkActionCode` + `applyActionCode`. */
export const APPLY_CODE_MODES = [
  "verifyEmail",
  "recoverEmail",
  "verifyAndChangeEmail",
  "revertSecondFactorAddition",
] as const;
export type ApplyCodeMode = (typeof APPLY_CODE_MODES)[number];

export type EmailAction =
  | { readonly kind: "resetPassword"; readonly oobCode: string; readonly tenantId: string | null }
  | {
      readonly kind: "applyCode";
      readonly mode: ApplyCodeMode;
      readonly oobCode: string;
      readonly tenantId: string | null;
    }
  | { readonly kind: "invalid"; readonly reason: "missing-code" | "unrecognized" };

/**
 * Identity Platform tenant ids are letters, digits and hyphens. Anything else
 * is a tampered link; rejecting it also keeps the value safe to put in the
 * "go to login" path.
 */
const TENANT_ID = /^[A-Za-z0-9-]+$/;

export function isAuthActionPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, "") === AUTH_ACTION_PATH;
}

function isApplyCodeMode(mode: string): mode is ApplyCodeMode {
  return (APPLY_CODE_MODES as ReadonlyArray<string>).includes(mode);
}

/** Classify the query string of an `/auth/action` link. */
export function parseEmailAction(search: string): EmailAction {
  const params = new URLSearchParams(search);
  const mode = params.get("mode") ?? "";
  const oobCode = params.get("oobCode");
  const rawTenantId = params.get("tenantId");
  if (rawTenantId !== null && !TENANT_ID.test(rawTenantId)) {
    return { kind: "invalid", reason: "unrecognized" };
  }
  const tenantId = rawTenantId || null;
  if (mode !== "resetPassword" && !isApplyCodeMode(mode)) {
    return { kind: "invalid", reason: "unrecognized" };
  }
  if (!oobCode) return { kind: "invalid", reason: "missing-code" };
  if (mode === "resetPassword") return { kind: "resetPassword", oobCode, tenantId };
  return { kind: "applyCode", mode, oobCode, tenantId };
}

/** Where "go to login" leads: the tenant's login, or the Access Hub without one. */
export function loginHref(tenantId: string | null): string {
  return tenantId ? `/${tenantId}/login` : "/";
}
